/* eslint-disable jsx-a11y/label-has-associated-control */
import cx from 'classnames';
import { h, Component } from 'preact';
import debounce from '../utils/debounce';

// components and uitls
import asyncComponent from '../components/AsyncComponent';
import Portal from '../components/Portal';
import Typeahead from '../components/Typeahead';
import { lookup } from '../utils/legislative-districts';

const sessionToken = new google.maps.places.AutocompleteSessionToken();
const autocompleteService = new google.maps.places.AutocompleteService();

const bounds = {
  east: -93.514764,
  west: -106.662177,
  south: 25.843902,
  north: 36.500199,
};

const componentRestrictions = { country: 'us' };

const types = ['geocode'];

const getPredictions = input => {
  return new Promise((resolve, reject) => {
    autocompleteService.getPlacePredictions(
      { bounds, componentRestrictions, input, sessionToken, types },
      (predict, status) => {
        if (status === 'OK') resolve(predict);

        reject(new Error('No results found.'));
      }
    );
  });
};

// always render what's provided, the geocoder decides
const shouldItemRender = () => true;

const AsyncCountyViewer = asyncComponent(() =>
  import(
    /* webpackPrefetch: true, webpackChunkName: "CountyViewer" */ '../components/CountyViewer'
  )
);

class Lookup extends Component {
  constructor() {
    super();

    this.state = {
      coordinates: null,
      data: null,
      lookupError: false,
      lookupPending: false,
      results: [],
      value: '',
    };

    this.handleGeocode = this.handleGeocode.bind(this);
    this.debouncedHandleGeocode = debounce(this.handleGeocode, 300);
    this.onChange = this.onChange.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onSelect = this.onSelect.bind(this);
  }

  componentDidMount() {
    this.placesService = new google.maps.places.PlacesService(this.inputRef);
  }

  onClear() {
    this.setState({ value: '' });
  }

  onChange(value) {
    this.setState({ value }, this.debouncedHandleGeocode);
  }

  handleGeocode() {
    const { value: input } = this.state;

    getPredictions(input)
      .then(results => this.setState({ results }))
      .catch(() => this.setState({ results: [] }));
  }

  onSelect({ name, placeId }) {
    this.setState({ lookupError: false, lookupPending: true, value: name });

    this.placesService.getDetails(
      {
        fields: ['geometry.location'],
        placeId,
        sessionToken,
      },
      result => {
        const {
          lat: latitude,
          lng: longitude,
        } = result.geometry.location.toJSON();

        const coordinates = { latitude, longitude };

        // looks up the county, sboe district, us house, tx house and tx senate districts
        lookup(coordinates)
          .then(({ data }) => {
            this.setState({ coordinates, data, lookupPending: false });
          })
          .catch(() =>
            this.setState({
              data: null,
              lookupError: true,
              lookupPending: false,
            })
          );
      }
    );
  }

  render() {
    const {
      coordinates,
      data,
      lookupError,
      lookupPending,
      results,
      value,
    } = this.state;

    let addressFeedback = null;

    if (lookupPending) {
      addressFeedback = (
        <p class="copy">
          Finding your details...{' '}
          <svg class="icon icon-spinner animate-pulse">
            <use xlinkHref="#icon-spinner" />
          </svg>
        </p>
      );
    }

    if (lookupError) {
      addressFeedback = (
        <p class="copy">
          Hmm... we were unable to find your county in Texas. Please check your
          address and try again!
        </p>
      );
    }

    if (coordinates && !lookupPending && !lookupError) {
      addressFeedback = <p class="copy t-weight-bold">We found your county!</p>;
    }

    return (
      <div class="address-input">
        {/* <svg class="icon icon-drawn-left-arrow">
          <use xlinkHref="#icon-drawn-left-arrow"></use>
        </svg> */}
        <label class="address-input__label t-sans" for="address-input">
          Enter an address or a county:
        </label>
        <Typeahead
          inputProps={{
            id: 'address-input',
            class: 'address-input__input',
            placeholder: '1010 Colorado St, Austin, TX, 78701',
          }}
          items={results}
          getItemValue={item => ({
            name: item.description,
            placeId: item.place_id,
          })}
          value={value}
          onChange={this.onChange}
          onClear={this.onClear}
          onSelect={this.onSelect}
          renderItem={(item, isHighlighted) => (
            <li
              class={cx('listbox__item', {
                'listbox__item--highlighted': isHighlighted,
              })}
              key={item.id}
            >
              {item.description}
            </li>
          )}
          renderAttribution={() => (
            <li class="listbox__item listbox__item--attribution">
              <img
                alt="Powered by Google"
                src="assets/images/powered-by-google.png"
              />
            </li>
          )}
          shouldItemRender={shouldItemRender}
          renderInput={props => (
            <input
              {...props}
              ref={el => {
                props.ref(el);
                this.inputRef = el;
              }}
            />
          )}
        />
        {/* <div class="disclaimer-lookup t-sans">
          * Turnout includes mail-in ballots received before Oct. 13.
        </div> */}
        <div id="address-feedback"></div>

        <Portal node={document.getElementById('address-feedback')}>
          {addressFeedback}
        </Portal>

        <Portal node={document.getElementById('lookup-county')}>
          {data ? (
            // you need this surrounding container! or else the whole thing explodes
            <div>
              <AsyncCountyViewer
                key={data.county.fips}
                county={data.county.name}
                fips={data.county.fips}
                section="lookup"
              />
            </div>
          ) : (
            ''
            // <div>
            //   <AsyncCountyViewer county="Total" section="lookup" />
            // </div>
          )}
        </Portal>
      </div>
    );
  }
}

export default Lookup;
